// Public Privacy Policy — modernized for Roava 2026.
// PUBLIC: no auth. Used as App Store Connect Privacy Policy URL.
// URL: https://roava.wgpapp.com/privacy

import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const EFFECTIVE_DATE = "June 2026";
const SUPPORT_EMAIL = "support@watsongrowthpartners.com";

export const Route = createFileRoute("/privacy")({
  head: () =>
    pageMeta({
      title: "Privacy Policy — Roava",
      description:
        "How Roava handles trips, location, photos, Family Follow, notifications, Apple Health, widgets, optional purchases, and account data.",
      path: "/privacy",
    }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <MarketingShell>
      <article className="mx-auto max-w-2xl pt-2">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Legal</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="mt-3 text-sm text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
        </header>

        <div className="space-y-6 text-sm sm:text-[15px] leading-relaxed text-foreground/90">
          <section>
            <p>
              Roava ("Roava", "we", "us") is a private, invite-only trip-coordination app
              from{" "}
              <a
                href="https://wgpapp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                WGP Apps
              </a>
              , part of the WGP umbrella brand. We collect only what we need to help your
              private trip group plan trips, record routes, share selected trip updates,
              and organize memories — including when connectivity is spotty.
            </p>
            <p className="mt-3">
              <strong>We do not sell personal information.</strong> We do not run ads. We do
              not share your trip data with advertisers or data brokers.
            </p>
          </section>

          <Section title="Information we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Account information:</strong> email address, display name, avatar,
                and profile details you provide.
              </li>
              <li>
                <strong>Trip and itinerary information:</strong> trips you create or join,
                itinerary entries, notes, and trip settings.
              </li>
              <li>
                <strong>Trip membership and invite codes:</strong> the trips you belong to
                and the invite codes used to join them.
              </li>
              <li>
                <strong>Location data during active trip recording:</strong> GPS points
                recorded while a trip is active and you have granted location permission.
                Background location is used only while a trip is being recorded.
              </li>
              <li>
                <strong>Queued and offline GPS points:</strong> location points captured on
                your device while offline, stored locally and synced when connectivity
                returns.
              </li>
              <li>
                <strong>Photos and videos you choose to upload</strong> to a trip library.
              </li>
              <li>
                <strong>Media metadata:</strong> capture date and time, file details, and
                location metadata embedded in the media when available.
              </li>
              <li>
                <strong>Family Follow link settings and access events:</strong> the share
                links you create, who they are scoped to, and basic events such as when a
                link is opened or revoked.
              </li>
              <li>
                <strong>Notification permission and device token,</strong> if you enable
                push notifications.
              </li>
              <li>
                <strong>Optional Apple Health / activity data,</strong> only if you grant
                permission and choose to export or attach activity context to a trip.
              </li>
              <li>
                <strong>Device and app diagnostics:</strong> app version, permission status,
                sync queue status, upload queue status, connectivity state, and error logs
                used to keep the app reliable.
              </li>
              <li>
                <strong>Purchase / support transaction status,</strong> only as needed to
                process optional in-app support purchases through Apple.
              </li>
            </ul>
          </Section>

          <Section title="How we use information">
            <ul className="list-disc pl-5 space-y-1">
              <li>Manage your trips, itineraries, and trip membership.</li>
              <li>Record and display your routes and trip activity.</li>
              <li>Support offline sync of routes, photos, and trip updates.</li>
              <li>Organize memories by time and, when available, location.</li>
              <li>
                Share selected trip updates with invited travelers or Family Follow viewers
                you have approved.
              </li>
              <li>
                Show trip status in Live Activities, Lock Screen views, the Dynamic Island,
                Home Screen widgets, and Control Center controls.
              </li>
              <li>Send optional notifications you have enabled.</li>
              <li>Support optional Apple Health / activity features when enabled.</li>
              <li>Improve reliability and security, and provide customer support.</li>
            </ul>
          </Section>

          <Section title="Location data and background tracking">
            <p>
              Location is used to record the route of trips you choose to start and to show
              <em> trip visibility</em> to people you share the trip with. Background
              location is only active while a trip is being recorded and only when you have
              granted the permission. You can stop recording at any time and you can change
              location permissions in iOS Settings.
            </p>
            <p className="mt-3">
              Roava is <strong>not</strong> an emergency, rescue, or safety-monitoring
              service. Trip visibility features are intended for coordination and memory,
              not safety-critical use.
            </p>
          </Section>

          <Section title="Offline sync and queued data">
            <p>
              When you are offline, Roava queues GPS points, photo uploads, and trip
              updates locally on your device so the trip record stays accurate in remote
              areas. Queued items are uploaded automatically when connectivity returns. You
              can also trigger a manual sync from inside the app.
            </p>
          </Section>

          <Section title="Photos, videos, and media metadata">
            <p>
              Photos and videos you upload to a trip are stored in that trip's private
              library and shared only with the travelers in that trip according to your
              sharing preferences. Roava reads media metadata such as capture date and
              embedded location (when available) to organize memories by time and place.
            </p>
            <p className="mt-3">
              Originals remain in your device Photos library unless you delete them
              yourself. Removing a photo from Roava does not remove it from your device.
            </p>
          </Section>

          <Section title="Family Follow and shared links">
            <p>
              Family Follow lets you generate an invite-only link that gives selected
              people a limited view of a trip. Share links are optional, scoped to what you
              choose to share, and <strong>revocable at any time</strong> from inside the
              app. We log basic access events (for example, when a link is opened or
              revoked) for your visibility and to keep the feature secure.
            </p>
          </Section>

          <Section title="Notifications">
            <p>
              Notifications are optional. If you enable push notifications, we register a
              notification device token to deliver trip-related alerts. Local notifications
              are scheduled on your device. You can disable notifications at any time in
              iOS Settings or inside Roava.
            </p>
          </Section>

          <Section title="Apple Health / HealthKit">
            <p>
              If you grant permission, Roava can read selected Apple Health / activity data
              to attach activity context to a trip, or export trip activity. Health data is
              only used for the features you enable.
            </p>
            <p className="mt-3">
              <strong>Roava does not delete Apple Health data from the Health app.</strong>{" "}
              You manage Apple Health data through the Health app and iOS Settings.
            </p>
          </Section>

          <Section title="Live Activities, widgets, and device storage">
            <p>
              To display trip status on the Lock Screen, in the Dynamic Island, in Home
              Screen widgets, and in Control Center, Roava writes a small amount of trip
              status data to a shared on-device storage area used by the app and its
              widgets/extensions. This data lives on your device.
            </p>
          </Section>

          <Section title="Optional in-app purchases">
            <p>
              Roava may offer optional in-app purchases to support development. These are
              not required to use the core app. Purchases are processed by Apple; Roava
              receives only the transaction status needed to enable any supporter-only
              cosmetics or unlocks.
            </p>
          </Section>

          <Section title="Data sharing">
            <p>
              We do not sell personal information. We share trip data only with the
              travelers and Family Follow viewers you have invited, and with service
              providers we use to operate the app (for example, secure cloud storage,
              Apple's notification service for push delivery, and Apple's payment system
              for purchases). Service providers are bound to handle data only as needed to
              run the service.
            </p>
          </Section>

          <Section title="Data retention">
            <p>
              We keep your account and trip data while your account is active. Diagnostic
              logs are retained for a limited period to help with reliability and support.
              When you delete your account, we remove your data as described on our{" "}
              <Link to="/account-deletion" className="text-primary hover:underline">
                Account & Data Deletion
              </Link>{" "}
              page.
            </p>
          </Section>

          <Section title="Account and data deletion">
            <p>
              You can request deletion of your account and associated Roava data at any
              time. See the{" "}
              <Link to="/account-deletion" className="text-primary hover:underline">
                Account & Data Deletion
              </Link>{" "}
              page for details on what is removed, what is not removed, and our response
              timeline.
            </p>
          </Section>

          <Section title="Security">
            <p>
              Trip data is stored on a secure cloud backend with per-user and per-trip
              access controls. Private media is held in private storage and served only to
              authorized travelers through short-lived signed links. No service can
              guarantee perfect security, but we work to apply reasonable safeguards.
            </p>
          </Section>

          <Section title="Children's privacy">
            <p>
              Roava is intended for adults coordinating travel within private groups. The
              app is not directed at children under 13, and we do not knowingly collect
              personal information from children under 13.
            </p>
          </Section>

          <Section title="Your choices">
            <ul className="list-disc pl-5 space-y-1">
              <li>Change location, photos, notification, and Health permissions in iOS Settings at any time.</li>
              <li>Revoke Family Follow links from inside the app.</li>
              <li>Stop trip recording at any time.</li>
              <li>Delete uploaded media you own from a trip library.</li>
              <li>
                Request account and data deletion via{" "}
                <Link to="/account-deletion" className="text-primary hover:underline">
                  Account & Data Deletion
                </Link>
                .
              </li>
            </ul>
          </Section>

          <Section title="Contact">
            <p>
              Questions about this policy? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              or visit{" "}
              <Link to="/support" className="text-primary hover:underline">
                Support
              </Link>
              .
            </p>
          </Section>
        </div>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-semibold tracking-tight mt-8 mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
